import { Button, SelectField, TextInput } from "@/components/ui";
import { frameTypeLabel } from "@/components/shapes/shared/frame-type-label";
import { useWallFrame } from "@/components/wall/wall-frame-source";
import { useT } from "@/i18n";
import { framesChartMessages } from "@/i18n/messages/frames-chart";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import type { ChartSource } from "./chart-points";
import {
  rangeChoice,
  type ChartRangeChoice,
  type ChartRangeState,
} from "./use-chart-range";

const CHOICES: ChartRangeChoice[] = ["all", "7d", "30d", "90d", "custom"];

/**
 * An opened chart's source line: muted "From <table>", which opens the Data
 * frame, and the period picker when the source names a date field (custom
 * periods take two native date inputs).
 */
export function ChartSourceBar({
  source,
  period,
}: {
  source: ChartSource;
  period: ChartRangeState;
}) {
  const t = useT(framesChartMessages);
  const table = useWallFrame(source.table);
  const labels: Record<ChartRangeChoice, string> = {
    all: t("allTime"),
    "7d": t("last7Days"),
    "30d": t("last30Days"),
    "90d": t("last90Days"),
    custom: t("customPeriod"),
  };
  const { range } = period;
  return (
    <div className="nodrag mt-[0.4em] flex flex-col gap-[0.4em]">
      <div className="flex min-w-0 items-center justify-between gap-[0.6em]">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto min-w-0 justify-start px-0 text-[0.78em] font-normal text-card-muted hover:text-card-ink"
          onClick={() => activateFrameInCurrentContext(source.table)}
          data-testid="chart-frame-source"
        >
          <span className="truncate">
            {t("fromTable", {
              title: table?.title.trim() || frameTypeLabel("dataset"),
            })}
          </span>
        </Button>
        {source.request.date_field && (
          <div className="w-[6.5em] shrink-0">
            <SelectField
              value={rangeChoice(range)}
              options={CHOICES.map((value) => ({
                value,
                label: labels[value],
              }))}
              onChange={(value) => period.pick(value as ChartRangeChoice)}
              ariaLabel={t("period")}
              testId="chart-range-picker"
            />
          </div>
        )}
      </div>
      {typeof range !== "string" && (
        <div className="flex gap-[0.4em]">
          <TextInput
            type="date"
            value={range.from}
            max={range.to || undefined}
            onChange={(e) => period.setDates(e.target.value, range.to)}
            aria-label={t("periodStart")}
            data-testid="chart-range-from"
          />
          <TextInput
            type="date"
            value={range.to}
            min={range.from || undefined}
            onChange={(e) => period.setDates(range.from, e.target.value)}
            aria-label={t("periodEnd")}
            data-testid="chart-range-to"
          />
        </div>
      )}
    </div>
  );
}
